// Seed dataset for local/demo mode. In live mode this is replaced wholesale by
// the tenant's real Supabase rows — the SAME code paths derive every vital,
// widget and chart from this array, so nothing in the UI is hardcoded.

export type Module = 'Incident' | 'Service Request' | 'Problem' | 'Change';
export type EventKind = 'system' | 'state' | 'comment' | 'sla';

export type TEvent = { at: number; label: string; kind: EventKind; actor?: string; internal?: boolean; source?: string };

export type Attachment = {
  id: string;
  name: string;
  size: number;
  type: string;
  url: string;
  path?: string;
};

export type Approval = {
  id: string;
  approver: string;
  status: 'pending' | 'approved' | 'rejected';
  note?: string;
  at?: number;
};

export type Ticket = {
  id: string;
  ref: string;
  module: Module;
  subject: string;
  state: string;
  requester: string;
  requesterId?: string | null;
  requesterEmail?: string | null;
  assignee: string | null;
  assigneeId?: string | null;
  team: string;
  teamId?: string | null;
  priority: 'P1' | 'P2' | 'P3' | 'P4';
  category: string;
  ci: string | null;
  createdAt: number;
  slaDueAt: number;
  watching: boolean;
  description: string;
  events: TEvent[];
  links?: string[]; // related ticket ids
  mergedInto?: string | null; // id of the ticket this was merged into
  impact?: string; // High | Medium | Low
  urgency?: string; // High | Medium | Low
  dueAt?: number | null;
  watchers?: string[];
  attachments?: Attachment[];
  custom?: Record<string, unknown>;
  resolutionCode?: string;
  closureNotes?: string;
  parentId?: string | null;
  responseDueAt?: number | null;
  escalationLevel?: number;
  approvals?: Approval[];
  createdBy?: string | null;
  organizationId?: string;
  organizationName?: string;
  location?: string;
  source?: string;
  sourceType?: string;
  threadId?: string;
};

export const RESOLUTION_CODES = [
  'Fixed',
  'Workaround provided',
  'Configuration change',
  'Duplicate',
  'Cannot reproduce',
  'No action required',
  'Known error',
];

const SCORE: Record<string, number> = { High: 3, Medium: 2, Low: 1 };
/** ITIL Impact × Urgency → Priority. */
export function derivePriority(impact?: string, urgency?: string): Ticket['priority'] | null {
  if (!impact || !urgency) return null;
  const s = (SCORE[impact] ?? 0) + (SCORE[urgency] ?? 0);
  if (s >= 6) return 'P1';
  if (s === 5) return 'P2';
  if (s === 4) return 'P3';
  return 'P4';
}

export const ME = 'A. Khan';
const RESOLVED = new Set(['Resolved', 'Closed']);
export const isOpen = (t: Ticket) => !RESOLVED.has(t.state);

const now = Date.now();
const H = 3_600_000;

function mk(
  ref: string,
  team: string,
  module: Module,
  subject: string,
  priority: Ticket['priority'],
  state: string,
  category: string,
  assignee: string | null,
  ageH: number,
  spanH: number,
  requester = 'requester@corp',
  ci: string | null = null,
): Ticket {
  const createdAt = now - ageH * H;
  return {
    id: ref.toLowerCase(),
    ref,
    module,
    subject,
    state,
    priority,
    requester,
    assignee,
    team,
    category,
    ci,
    createdAt,
    slaDueAt: createdAt + spanH * H,
    watching: false,
    description: `${subject}. Reported by ${requester}.`,
    events: [{ at: createdAt, kind: 'system', label: 'Ticket created', actor: requester }],
  };
}

export const MOCK_TICKETS: Ticket[] = [
  // Platform SRE: 3 OPEN, 3 BREACHING, 0 UNASSIGNED, AVG RES: 26.3h. 
  mk('INC-2001', 'Platform SRE', 'Incident', 'Database high latency', 'P1', 'In Progress', 'Performance', 'M. Rao', 26.3, 30, 'dba@corp'),
  mk('INC-2002', 'Platform SRE', 'Incident', 'Kubernetes node not ready', 'P2', 'In Progress', 'Infrastructure', 'L. Chen', 26.3, 30, 'dev@corp'),
  mk('INC-2003', 'Platform SRE', 'Incident', 'Cache misses rising', 'P2', 'In Progress', 'Performance', 'A. Khan', 26.3, 30, 'dev@corp'),

  // IT / Service Desk: 4 OPEN, 1 BREACHING, 2 UNASSIGNED, AVG RES: 2.4h
  {...mk('INC-2004', 'IT / Service Desk', 'Incident', 'Network dropout', 'P2', 'Triage', 'Network', null, 4.6, 5, 'staff@corp'), source: 'Email'},
  mk('INC-2005', 'IT / Service Desk', 'Incident', 'Cannot print', 'P4', 'In Progress', 'Hardware', 'P. Diaz', 3.0, 8, 'staff@corp'),
  mk('REQ-2006', 'IT / Service Desk', 'Service Request', 'New laptop request', 'P3', 'In Progress', 'Hardware', 'R. Vogt', 1.0, 4, 'staff@corp'),
  {...mk('INC-2007', 'IT / Service Desk', 'Incident', 'Password reset', 'P3', 'New', 'Access', null, 1.0, 4, 'staff@corp'), source: 'Email'},

  // People / HR: 2 OPEN, 0 BREACHING, 1 UNASSIGNED, AVG RES: 19h
  mk('REQ-2008', 'People / HR', 'Service Request', 'Benefits question', 'P4', 'New', 'Benefits', 'C. Mensah', 19, 40, 'staff@corp'),
  mk('REQ-2009', 'People / HR', 'Service Request', 'Offer letter', 'P3', 'New', 'Recruiting', null, 19, 40, 'manager@corp'),

  // Legal: 1 OPEN, 0 BREACHING, 0 UNASSIGNED, AVG RES: 30h
  mk('REQ-2010', 'Legal', 'Service Request', 'Contract review', 'P3', 'In Progress', 'Legal', 'I. Volkov', 30, 100, 'manager@corp'),

  // Design: 2 OPEN, 0 BREACHING, 1 UNASSIGNED, AVG RES: 22h
  mk('REQ-2011', 'Design', 'Service Request', 'New logo assets', 'P3', 'In Progress', 'Assets', 'E. Lind', 22, 50, 'marketing@corp'),
  mk('REQ-2012', 'Design', 'Service Request', 'Slide deck template', 'P4', 'New', 'Assets', null, 22, 50, 'sales@corp'),

  // Development: 3 OPEN, 0 BREACHING, 1 UNASSIGNED, AVG RES: 10.4h
  mk('INC-2013', 'Development', 'Incident', 'Checkout bug', 'P2', 'In Progress', 'Bug', 'S. Patel', 10.4, 30, 'support@corp'),
  mk('REQ-2014', 'Development', 'Service Request', 'New API endpoint', 'P3', 'In Progress', 'Feature', 'J. Wu', 10.4, 30, 'product@corp'),
  mk('INC-2015', 'Development', 'Incident', 'Test failing', 'P3', 'New', 'Bug', null, 10.4, 30, 'ci@corp'),

  // Finance: 1 OPEN, 0 BREACHING, 0 UNASSIGNED, AVG RES: 12h
  mk('REQ-2016', 'Finance', 'Service Request', 'Expense report', 'P4', 'New', 'Expense', 'O. Haas', 12, 30, 'staff@corp'),
];

export const LANES = [
  { id: 'my-queue', label: 'My Queue' },
  { id: 'team', label: 'Team' },
  { id: 'watching', label: 'Watching' },
  { id: 'breaching', label: 'Breaching Soon' },
] as const;

export type LaneId = (typeof LANES)[number]['id'];

export function heatOf(t: Ticket, atMs = Date.now()): number {
  const span = t.slaDueAt - t.createdAt;
  if (span <= 0) return 1;
  return Math.max(0, Math.min(1, (atMs - t.createdAt) / span));
}

export function ticketsInLane(tickets: Ticket[], lane: LaneId, atMs = Date.now()): Ticket[] {
  switch (lane) {
    case 'my-queue':
      return tickets.filter((t) => t.assignee === ME);
    case 'watching':
      return tickets.filter((t) => t.watching);
    case 'breaching':
      return tickets.filter((t) => heatOf(t, atMs) >= 0.65).sort((a, b) => heatOf(b, atMs) - heatOf(a, atMs));
    case 'team':
    default:
      return tickets;
  }
}
